import { defineComponent as _defineComponent } from 'vue'
import Collapse from "@/components/common/Collapse.vue";
import { AppMode, CreditSummary, Release, User } from "@/CodegenTypes";
import { formatDollar } from "@/helpers/formatDollar";
import { customMoment } from "@/main";
import { FEATURE_TOGGLES } from "@/config";
import { canSeeDdCompanyLevel } from "@/helpers/user/canSeeDdCompanyLevel";
import { canSeeTtCompanyLevel } from "@/helpers/user/canSeeTtCompanyLevel";
import { useRootStore } from "@/store/pinia";
import { computed, ref } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRouter } from "vue-router/composables";
import { hybridQueries } from "@/apollo/hybrid-queries";
import { addWarnToast } from "@/vue-service";
import { useTranslation } from "@/store/translation";

type SideNavUser = Pick<
  User,
  | "userId"
  | "roleName"
  | "createAgent"
  | "isSuperUser"
  | "ddEnabled"
  | "ttEnabled"
  | "ddCompanyPl"
  | "ttD1CompanyPl"
  | "ttD2CompanyPl"
  | "ttD3CompanyPl"
  | "ttD4CompanyPl"
  | "paymentPayableSum"
> & {
  creditSummary: Pick<
    CreditSummary,
    "creditGiven" | "creditGivenToDownlines" | "creditBalance"
  >;
};

type SideNavRelease = Pick<
  Release,
  "id" | "releaseDate" | "windowCloseAt" | "windowIsOpen"
> &
  Partial<Pick<Release, "isOver">>;


export default /*#__PURE__*/_defineComponent({
  __name: 'SideNav',
  setup(__props) {

const { $t } = useTranslation();

const { result, loading } = useQuery<{
  me: SideNavUser;
  releases: SideNavRelease[];
}>(
  hybridQueries.sideNav,
  { appMode: computed(() => useRootStore().appMode) },
  {
    pollInterval: 60000,
  }
);

const appMode = computed(() => {
  return useRootStore().appMode;
});

const selectedOption = computed(() => {
  return appModeOptions.value.find((opt) => opt.value === appMode.value);
});

const paymentPayable = computed(() => {
  return result.value?.me.paymentPayableSum || 0;
});

const isDd = computed(() => {
  return useRootStore().appMode === AppMode.Dd;
});

const isTt = computed(() => {
  return useRootStore().appMode === AppMode.Tt;
});

const menus = computed(() => {
  if (!result.value?.me) {
    return [];
  }
  return [
    {
      groupName: $t("Main"),
      show: true,
      items: [
        {
          show: true,
          label: $t("Announcements"),
          route: { name: "home" },
        },
        {
          show: true,
          label: $t("My Profile"),
          route: { name: "profile-profile" },
        },
        {
          show: true,
          label: $t("My Downlines"),
          route: {
            name: "profile-downlines",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: result.value?.me.createAgent,
          label: $t("Payment"),
          route: {
            name: "profile-payment",
            query: { userId: result.value?.me.userId },
          },
        },
      ],
    },
    {
      groupName: $t("Bet Entry"),
      show: true,
      items: [
        // {
        //   show: isDd.value && FEATURE_TOGGLES.showSimpleEntry,
        //   label: $t("Simple Entry"),
        //   route: { name: "simple entry" },
        // },
        {
          show: isTt.value && FEATURE_TOGGLES.showSimpleEntry,
          label: $t("Simple Entry"),
          route: {
            name: "toto simple entry",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isDd.value && FEATURE_TOGGLES.showMassEntryV1,
          label: $t("Bet Entry Dark"),
          route: {
            name: "mass entry",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isDd.value && FEATURE_TOGGLES.showMassEntryV2,
          label: $t("Bet Entry"),
          route: {
            name: "mass entry 2",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isTt.value && FEATURE_TOGGLES.showMassEntryV1,
          label: $t("Advance Bet Dark"),
          route: {
            name: "tt mass entry",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isTt.value && FEATURE_TOGGLES.showMassEntryV2,
          label: $t("Advance Bet"),
          route: {
            name: "tt mass entry 2",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isTt.value && FEATURE_TOGGLES.showSavedBet,
          label: $t("Saved Bet"),
          route: {
            name: "tt saved bet",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isTt.value,
          label: $t("Fixed Bet"),
          route: {
            name: "tt fixed bet",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isDd.value && FEATURE_TOGGLES.showSavedBet,
          label: $t("Saved Bet"),
          route: {
            name: "saved bet",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isDd.value,
          label: $t("Fixed Bet"),
          route: {
            name: "fixed bet",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isDd.value,
          label: $t("File Upload"),
          route: {
            name: "upload entry",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isTt.value,
          label: $t("File Upload"),
          route: {
            name: "tt upload entry",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isDd.value,
          label: $t("Telegram Help"),
          route: { name: "telegram help" },
        },
        {
          show: isTt.value,
          label: $t("Telegram Help"),
          route: { name: "toto telegram help" },
        },
        {
          show: isDd.value,
          label: $t("Payout Guide"),
          route: { name: "dd payout guide" },
        },
        {
          show: isTt.value,
          label: $t("Payout Guide"),
          route: { name: "tt payout guide" },
        },
      ],
    },
    {
      groupName: $t("Bet List"),
      show: true,
      items: [
        {
          show: isDd.value,
          label: $t("Bet Details"),
          route: {
            name: "page details",
            query: { userId: result.value?.me.userId },
          },
        },
        // {
        //   show: isDd.value,
        //   label: "Bet Details",
        //   route: { name: "bet details", query: { userId: result.value?.me.userId } }
        // },
        {
          show: isTt.value,
          label: $t("Bet Details"),
          route: {
            name: "tt page details",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isDd.value,
          label: $t("Deleted Bet Details"),
          route: {
            name: "deleted bet details",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isTt.value,
          label: $t("Deleted Bet Details"),
          route: {
            name: "toto deleted bet details",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isDd.value,
          label: $t("Intake Details"),
          route: {
            name: "intake details",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isTt.value,
          label: $t("Intake Details"),
          route: {
            name: "toto intake details",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isDd.value && seeDdCompanyLevel.value,
          label: $t("Stake File"),
          route: { name: "stake file" },
        },
        {
          show: isTt.value && seeTtCompanyLevel.value,
          label: $t("Stake File"),
          route: { name: "toto stake file" },
        },
      ],
    },
    {
      groupName: $t("Report"),
      show: true,
      items: [
        {
          show: isDd.value && seeDdCompanyLevel.value,
          label: $t("Company Profit/Loss"),
          route: { name: "companypl report" },
        },
        {
          show: isTt.value && seeTtCompanyLevel.value,
          label: $t("Company Profit/Loss"),
          route: { name: "toto companypl report" },
        },
        {
          show:
            isDd.value &&
            seeDdCompanyLevel.value &&
            FEATURE_TOGGLES.useV1Legacy,
          label: $t("Company Stake Report"),
          route: { name: "stake report" },
        },
        {
          show:
            isTt.value &&
            seeTtCompanyLevel.value &&
            FEATURE_TOGGLES.useV1Legacy,
          label: $t("Company Stake Report"),
          route: { name: "toto stake report" },
        },
        {
          show: isDd.value,
          label: $t("Company Full Report"),
          route: {
            name: "full report",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isTt.value,
          label: $t("Company Full Report"),
          route: {
            name: "toto full report",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isDd.value && FEATURE_TOGGLES.useV1Legacy,
          label: $t("Collection"),
          route: {
            name: "collection report",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isTt.value && FEATURE_TOGGLES.useV1Legacy,
          label: $t("Collection"),
          route: {
            name: "toto collection report",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          // show: (isTt.value || isDd.value) && (result.value?.me.userId === 'h' || result.value?.me.userId === 'admin'),
          show: isTt.value || isDd.value,
          label: $t("Summary Report"),
          route: {
            name: "summary report",
            // query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isDd.value,
          label: $t("Strike Report"),
          route: {
            name: "strike report",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isTt.value,
          label: $t("Strike Report"),
          route: {
            name: "toto strike report",
            query: { userId: result.value?.me.userId },
          },
        },
        {
          show: isTt.value,
          label: $t("Draw Results"),
          route: {
            name: "toto results",
          },
        },
        {
          show: isDd.value,
          label: $t("Draw Results"),
          route: {
            name: "4d results",
          },
        },
      ],
    },
    // {
    //   groupName: "Account",
    //   show: true,
    //   items: [

    //   ]
    // },
    {
      groupName: $t("Admin"),
      show: result.value?.me.roleName === "admin",
      items: [
        {
          show: true,
          label: $t("Settings"),
          route: { name: "admin-settings" },
        },
        {
          show: isDd.value && result.value?.me.isSuperUser,
          label: $t("Outset"),
          route: { name: "admin-outset" },
        },
        {
          show: isTt.value && result.value?.me.isSuperUser,
          label: $t("Outset"),
          route: { name: "toto-admin-outset" },
        },
        {
          show: isDd.value && result.value?.me.isSuperUser,
          label: $t("Control Panel"),
          route: { name: "admin-controlpanel" },
        },
        {
          show: isTt.value && result.value?.me.isSuperUser,
          label: $t("Control Panel"),
          route: { name: "admin-toto-controlpanel" },
        },
      ],
    },
    {
      groupName: $t("View Log"),
      show: true,
      items: [
        {
          show: true,
          label: $t("Login History"),
          route: { name: "log-loginhistory" },
        },
        {
          show: true,
          label: $t("Audit Log"),
          route: { name: "log-auditlog" },
        },
      ],
    },
  ];
});

const activeCollapse = ref("Main");

const appModeOptions = computed(() => {
  const opts = [];
  if (result.value?.me.ddEnabled) {
    opts.push({ label: $t("4D"), value: AppMode.Dd });
  }
  if (result.value?.me.ttEnabled) {
    opts.push({ label: $t("TOTO"), value: AppMode.Tt });
  }
  return opts;
});

const filteredReleases = computed(() => {
  return result.value?.releases.filter((release) => !release.isOver);
});

const menuItemClass = computed(() => {
  return FEATURE_TOGGLES.useMenuColours
    ? "hover:bg-primary-50"
    : "hover:bg-gray-200";
});

const seeDdCompanyLevel = computed(() => {
  return canSeeDdCompanyLevel(result.value?.me);
});

const seeTtCompanyLevel = computed(() => {
  return canSeeTtCompanyLevel(result.value?.me);
});

const router = useRouter();

function switchAppMode(value?: AppMode) {
  if (value) {
    router.push({ name: "home" });
    useRootStore().appMode = value;
  } else {
    router.push({ name: "home" });
    const otherAppMode = appModeOptions.value.find(
      (opt) => opt.value !== appMode.value
    );

    if (otherAppMode) {
      useRootStore().appMode = otherAppMode.value;
    } else {
      addWarnToast("No other game modes available");
    }
  }
}

function formatDate(dateStr: string) {
  return customMoment(dateStr).format("ddd, DD MMM yyyy");
}
function formatDatetime(dateStr: string) {
  return customMoment(dateStr).format("ddd, DD MMM yyyy hh:mma");
}

function setCollapse(field: string, val: boolean) {
  if (val) {
    activeCollapse.value = field;
  } else {
    activeCollapse.value = "";
  }
}

function menuItemTestId(value: unknown) {
  return String(value || "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

return { __sfc: true,$t, result, loading, appMode, selectedOption, paymentPayable, isDd, isTt, menus, activeCollapse, appModeOptions, filteredReleases, menuItemClass, seeDdCompanyLevel, seeTtCompanyLevel, router, switchAppMode, formatDate, formatDatetime, setCollapse, menuItemTestId, Collapse, formatDollar, FEATURE_TOGGLES }
}

})